import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ErrorAlert } from '../../components/ErrorAlert';
import {
  useGenerateQrBatch,
  useStickerHeaderTemplatesList,
} from '../../api/queries';

/**
 * "Generate SC QR batch" — mirrors the per-machine sticker
 * CreateBatchDialog. Free-form count, layout preset → paper / dims /
 * grid, optional header template (reuses platform.sticker_header_template).
 *
 * Each pair prints as TWO labels (DEVICE + PACKAGING) so the sheet-count
 * estimate uses `count × 2` against the sheet capacity.
 */

type Preset = {
  label: string;
  paper: string;
  widthMm: number;
  heightMm: number;
  cols: number;
  rows: number;
};

const PRESETS: Preset[] = [
  // SC pair labels are larger than per-machine stickers — A6-ish pair
  // labels print 10 per A4 (2 cols × 5 rows). Each pair = 2 labels, so
  // a 50-pair batch is 100 labels = 10 A4 sheets.
  { label: 'SC pair, 10 per A4 (default)',  paper: 'A4', widthMm: 100, heightMm:  60, cols: 2, rows: 5 },
  { label: 'SC pair, 4 per A4 (large)',     paper: 'A4', widthMm: 100, heightMm: 143, cols: 2, rows: 2 },
  { label: 'A6 test (4 per A4)',            paper: 'A4', widthMm: 100, heightMm: 143, cols: 2, rows: 2 },
  { label: 'Avery L7159 (24 per A4)',       paper: 'A4', widthMm:  64, heightMm:  34, cols: 3, rows: 8 },
  { label: 'Single A6 per page',            paper: 'A6', widthMm:  95, heightMm: 138, cols: 1, rows: 1 },
  { label: 'Custom',                        paper: 'A4', widthMm: 100, heightMm:  60, cols: 2, rows: 5 },
];

export function GenerateQrBatchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create     = useGenerateQrBatch();
  const headers    = useStickerHeaderTemplatesList();
  const headersQ   = useQuery({ queryKey: headers.queryKey, queryFn: headers.listFn, staleTime: 60_000 });

  const [code, setCode]                       = useState('');
  const [count, setCount]                     = useState<number>(50);
  const [presetIdx, setPresetIdx]             = useState(0);
  const [paper, setPaper]                     = useState(PRESETS[0].paper);
  const [widthMm, setWidthMm]                 = useState(PRESETS[0].widthMm);
  const [heightMm, setHeightMm]               = useState(PRESETS[0].heightMm);
  const [cols, setCols]                       = useState(PRESETS[0].cols);
  const [rows, setRows]                       = useState(PRESETS[0].rows);
  const [headerTemplateId, setHeaderTemplate] = useState<string>('');
  const [notes, setNotes]                     = useState('');
  const [error, setError]                     = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCode(''); setCount(50); setPresetIdx(0);
      setPaper(PRESETS[0].paper); setWidthMm(PRESETS[0].widthMm);
      setHeightMm(PRESETS[0].heightMm); setCols(PRESETS[0].cols);
      setRows(PRESETS[0].rows); setHeaderTemplate(''); setNotes(''); setError(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function applyPreset(idx: number) {
    const p = PRESETS[idx];
    setPresetIdx(idx);
    setPaper(p.paper); setWidthMm(p.widthMm); setHeightMm(p.heightMm);
    setCols(p.cols);   setRows(p.rows);
  }

  const valid = code.trim().length > 0 && count > 0 && count <= 10_000
    && widthMm > 0 && heightMm > 0 && cols > 0 && rows > 0;
  const labelsPerSheet = cols * rows;
  const totalLabels    = count * 2;   // each pair = DEVICE + PACKAGING
  const sheetEstimate  = labelsPerSheet > 0 ? Math.ceil(totalLabels / labelsPerSheet) : 0;

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        code:             code.trim(),
        count,
        labelPaper:       paper,
        labelWidthMm:     widthMm,
        labelHeightMm:    heightMm,
        colsPerSheet:     cols,
        rowsPerSheet:     rows,
        headerTemplateId: headerTemplateId || null,
        notes:            notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Generate failed.');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Generate site-controller QR batch
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Mints {count.toLocaleString()} signed QR pairs. Each pair prints
            as <strong>two</strong> labels (DEVICE on the CM5 base board +
            PACKAGING on the shipping box) carrying the same payload — so
            this batch produces {totalLabels.toLocaleString()} labels
            ≈ {sheetEstimate} sheet{sheetEstimate === 1 ? '' : 's'} of {paper} at {cols}×{rows} per sheet.
          </Typography>

          <TextField
            label="Batch code"
            placeholder="e.g. SC-2026-04-001"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required fullWidth size="small"
            helperText="Human-readable identifier; must be unique."
          />

          <TextField
            label="Pair count"
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, parseInt(e.target.value || '0', 10)))}
            inputProps={{ min: 1, max: 10000 }}
            fullWidth size="small"
            helperText="1 to 10,000 pairs. Each pair = 2 labels."
          />

          <TextField
            select label="Layout preset" value={presetIdx}
            onChange={(e) => applyPreset(parseInt(e.target.value as unknown as string, 10))}
            fullWidth size="small"
          >
            {PRESETS.map((p, i) => (
              <MenuItem key={p.label} value={i}>{p.label}</MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1}>
            <TextField select label="Paper" value={paper}
              onChange={(e) => setPaper(e.target.value)}
              size="small" sx={{ flex: 1 }}>
              {['A4', 'A5', 'A6', 'Letter'].map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
            <TextField label="Width (mm)" type="number" value={widthMm}
              onChange={(e) => setWidthMm(parseFloat(e.target.value || '0'))}
              size="small" sx={{ flex: 1 }} />
            <TextField label="Height (mm)" type="number" value={heightMm}
              onChange={(e) => setHeightMm(parseFloat(e.target.value || '0'))}
              size="small" sx={{ flex: 1 }} />
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField label="Cols / sheet" type="number" value={cols}
              onChange={(e) => setCols(Math.max(1, parseInt(e.target.value || '0', 10)))}
              size="small" sx={{ flex: 1 }} />
            <TextField label="Rows / sheet" type="number" value={rows}
              onChange={(e) => setRows(Math.max(1, parseInt(e.target.value || '0', 10)))}
              size="small" sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary"
              sx={{ flex: 1, alignSelf: 'center' }}>
              {labelsPerSheet} per sheet → {sheetEstimate} sheet(s)
            </Typography>
          </Stack>

          <TextField
            select label="QR Code header template (optional)"
            value={headerTemplateId}
            onChange={(e) => setHeaderTemplate(e.target.value)}
            size="small" fullWidth
            helperText="Reuses the QR-code header templates from /stickers/header-templates."
          >
            <MenuItem value=""><em>— No header —</em></MenuItem>
            {(headersQ.data ?? []).map((h) => (
              <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline minRows={2} fullWidth size="small"
          />

          {error && <ErrorAlert>{error}</ErrorAlert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!valid || create.isPending} onClick={submit}>
          {create.isPending ? 'Generating…' : 'Generate batch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
